import { useRouteLoaderData } from 'react-router-dom';
import { useAppSelector } from '../../stores/redux.store';
import type { MfaAllowedMethod, SwitchableMfaFactor } from '../../types';
import { isRedirect } from '../fetch-api';
import type { ServerSideRedirect } from '../fetch-api';
import type { PreloadedAppState } from '../init-app-state';

export const MFA_ENROLLMENT_ROUTE_ID = 'mfa-enrollment';

export const useMfaEnrollmentAllowedMethods = (): MfaAllowedMethod[] | undefined => {
  const loaderData = useRouteLoaderData(MFA_ENROLLMENT_ROUTE_ID) as
    | PreloadedAppState
    | ServerSideRedirect
    | undefined;
  const legacyEnrollmentPolicy = useAppSelector(state => state.mfaEnrollment);

  // An absent allowedMethods field in a successful response explicitly means unrestricted.
  if (loaderData && !isRedirect(loaderData)) {
    return loaderData.allowedMethods;
  }

  // Preserve existing tests, stories and legacy entry points which still hydrate Redux.
  if (legacyEnrollmentPolicy.mfaType === 'SecurityKey') {
    return legacyEnrollmentPolicy.keyProtection
      ? [
          {
            type: 'SecurityKey',
            protectionType: legacyEnrollmentPolicy.keyProtection,
          },
        ]
      : undefined;
  }

  return legacyEnrollmentPolicy.mfaType ? [{ type: legacyEnrollmentPolicy.mfaType }] : undefined;
};

export const isMfaEnrollmentMethodAllowed = (
  allowedMethods: MfaAllowedMethod[] | undefined,
  type: SwitchableMfaFactor
): boolean => !allowedMethods || allowedMethods.some(method => method.type === type);

export const getAllowedSecurityKeyMethod = (
  allowedMethods: MfaAllowedMethod[] | undefined
): Extract<MfaAllowedMethod, { type: 'SecurityKey' }> | undefined =>
  allowedMethods?.find(
    (method): method is Extract<MfaAllowedMethod, { type: 'SecurityKey' }> =>
      method.type === 'SecurityKey'
  );
